import { useState, useEffect } from "react";
import axios from "axios";
import {
  ListChecks,
  Plus,
  Pencil,
  Trash,
  MagnifyingGlass,
  X,
  DotsSixVertical,
  Prohibit,
  ArrowCounterClockwise,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const DEFAULT_ITEMS = [
  { name: "motor_instalado", label: "Motor instalado" },
  { name: "freios_verificados", label: "Freios verificados" },
  { name: "luzes_funcionando", label: "Luzes funcionando" },
  { name: "painel_conectado", label: "Painel conectado" },
  { name: "acelerador_testado", label: "Acelerador testado" },
  { name: "pneus_calibrados", label: "Pneus calibrados" },
  { name: "bateria_fixada", label: "Baterias fixadas" },
  { name: "carenagem_fechada", label: "Carenagem fechada" },
];

export default function ChecklistTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [search, setSearch] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    items: DEFAULT_ITEMS.map((item) => ({ ...item })),
  });
  const [newItemName, setNewItemName] = useState("");
  const [newItemLabel, setNewItemLabel] = useState("");

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(
        `${API}/checklist-templates?active_only=false`,
        {
          withCredentials: true,
        },
      );
      setTemplates(response.data);
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.items.length === 0) {
      toast.error("Adicione pelo menos um item ao checklist");
      return;
    }

    try {
      const data = {
        name: formData.name,
        description: formData.description || null,
        items: formData.items,
      };

      if (editingTemplate) {
        await axios.put(
          `${API}/checklist-templates/${editingTemplate.id}`,
          data,
          { withCredentials: true },
        );
        toast.success("Template atualizado com sucesso!");
      } else {
        await axios.post(`${API}/checklist-templates`, data, {
          withCredentials: true,
        });
        toast.success("Template criado com sucesso!");
      }

      setShowForm(false);
      setEditingTemplate(null);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar template");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      items: DEFAULT_ITEMS.map((item) => ({ ...item })),
    });
    setNewItemName("");
    setNewItemLabel("");
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    const items = Object.entries(template.items).map(([name, data]) => ({
      name,
      label: data.label,
    }));
    setFormData({
      name: template.name || "",
      description: template.description || "",
      items,
    });
    setShowForm(true);
  };

  const handleToggleActive = (item) => {
    const isCurrentlyActive = item.active !== false;
    const actionText = isCurrentlyActive ? "desativar" : "reativar";

    toast(`${isCurrentlyActive ? "Desativar" : "Reativar"} template?`, {
      description: `Deseja realmente ${actionText} "${item.name}"?`,
      action: {
        label: `Sim, ${actionText}`,
        onClick: async () => {
          try {
            if (isCurrentlyActive) {
              await axios.delete(`${API}/checklist-templates/${item.id}`, {
                withCredentials: true,
              });
            } else {
              await axios.put(
                `${API}/checklist-templates/${item.id}`,
                { active: true },
                { withCredentials: true },
              );
            }
            toast.success(
              `Template ${isCurrentlyActive ? "desativado" : "reativado"}!`,
            );
            fetchTemplates();
          } catch (error) {
            toast.error(`Erro ao ${actionText}`);
          }
        },
      },
      cancel: { label: "Cancelar" },
      duration: 5000,
    });
  };

  const addItem = () => {
    if (!newItemName.trim() || !newItemLabel.trim()) {
      toast.error("Preencha o código e a descrição do item");
      return;
    }

    const name = newItemName.trim().toLowerCase().replace(/\s+/g, "_");

    if (formData.items.some((item) => item.name === name)) {
      toast.error("Item já existe no checklist");
      return;
    }

    setFormData({
      ...formData,
      items: [...formData.items, { name, label: newItemLabel.trim() }],
    });
    setNewItemName("");
    setNewItemLabel("");
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
  };

  const filteredTemplates = templates.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase()),
  );

  if (loading) {
    return (
      <Layout title="Checklists">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Templates de Checklist">
      <div className="space-y-4 pb-4">
        {/* Search */}
        <div className="relative w-full">
          <MagnifyingGlass
            size={20}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] shrink-0"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field !pl-12 h-12 w-full"
            placeholder="Buscar template..."
            data-testid="search-templates"
          />
        </div>

        {/* Templates List */}
        {filteredTemplates.length === 0 ? (
          <div className="card p-8 text-center">
            <ListChecks size={48} className="mx-auto text-[#A1A1AA] mb-4" />
            <p className="text-[#A1A1AA] mb-4">Nenhum template cadastrado</p>
            <p className="text-xs text-[#A1A1AA]">
              Crie templates de checklist para diferentes modelos de motos
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className={`card p-4 sm:p-5 transition-all ${template.active === false ? "opacity-50 grayscale-[50%]" : ""}`}
                data-testid={`template-${template.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#00E676]/10 flex items-center justify-center flex-shrink-0">
                      <ListChecks
                        size={20}
                        className="text-[#00E676] sm:w-6 sm:h-6"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-base sm:text-lg truncate flex items-center flex-wrap gap-2">
                        {template.name}
                        {template.active === false && (
                          <span className="text-[10px] uppercase font-bold bg-red-500/20 text-red-500 px-2 py-0.5 rounded border border-red-500/20">
                            Desativado
                          </span>
                        )}
                      </h3>
                      {template.description && (
                        <p className="text-xs sm:text-sm text-[#A1A1AA] mt-1 line-clamp-2">
                          {template.description}
                        </p>
                      )}
                      <p className="text-xs text-[#A1A1AA] mt-1.5 font-medium">
                        {Object.keys(template.items).length} itens
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 shrink-0 border-t border-white/5 sm:border-0 pt-3 sm:pt-0">
                    <button
                      onClick={() => handleEdit(template)}
                      className="w-10 h-10 sm:w-auto sm:px-4 rounded-lg bg-[#1A1A1A] border border-white/10 flex items-center justify-center hover:bg-[#222] transition-colors gap-2"
                      data-testid={`edit-template-${template.id}`}
                    >
                      <Pencil size={18} className="text-[#A1A1AA]" />
                      <span className="hidden sm:inline text-sm text-[#A1A1AA]">
                        Editar
                      </span>
                    </button>
                    <button
                      onClick={() => handleToggleActive(template)}
                      className={`w-10 h-10 sm:w-auto sm:px-4 rounded-lg border flex items-center justify-center transition-colors gap-2 ${
                        template.active !== false
                          ? "bg-red-500/10 border-red-500/20 hover:bg-red-500/20 text-red-500"
                          : "bg-blue-500/10 border-blue-500/20 hover:bg-blue-500/20 text-blue-500"
                      }`}
                    >
                      {template.active !== false ? (
                        <>
                          <Prohibit size={18} />
                          <span className="hidden sm:inline text-sm">
                            Desativar
                          </span>
                        </>
                      ) : (
                        <>
                          <ArrowCounterClockwise size={18} />
                          <span className="hidden sm:inline text-sm">
                            Reativar
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* FAB */}
        <button
          onClick={() => {
            setEditingTemplate(null);
            resetForm();
            setShowForm(true);
          }}
          className="fixed bottom-24 right-4 w-14 h-14 bg-[#00E676] rounded-full flex items-center justify-center shadow-[0_0_15px_rgba(0,230,118,0.3)] hover:bg-[#00C853] hover:scale-105 transition-all z-20"
          data-testid="add-template-button"
        >
          <Plus size={28} weight="bold" className="text-black" />
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-[#121212] rounded-xl p-5 sm:p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto border border-white/10 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-lg">
                {editingTemplate
                  ? "Editar Template"
                  : "Novo Template de Checklist"}
              </h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingTemplate(null);
                }}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#1A1A1A] transition-colors"
              >
                <X size={20} className="text-[#A1A1AA]" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs text-[#A1A1AA] block mb-1.5 ml-1">
                  Nome do Template *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="input-field w-full"
                  placeholder="Ex: Checklist Voltz EVS"
                  required
                  data-testid="template-name-input"
                />
              </div>

              <div>
                <label className="text-xs text-[#A1A1AA] block mb-1.5 ml-1">
                  Descrição
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="input-field w-full"
                  placeholder="Descrição opcional"
                  data-testid="template-description-input"
                />
              </div>

              {/* Items List */}
              <div className="pt-2">
                <label className="text-sm font-bold block mb-3 text-white">
                  Itens do Checklist{" "}
                  <span className="text-[#00E676] bg-[#00E676]/10 px-2 py-0.5 rounded text-xs ml-2">
                    {formData.items.length}
                  </span>
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
                  {formData.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 bg-[#1A1A1A] p-3 rounded-lg border border-transparent hover:border-white/5 transition-colors"
                    >
                      <DotsSixVertical
                        size={18}
                        className="text-[#A1A1AA] flex-shrink-0 cursor-move"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {item.label}
                        </p>
                        <p className="text-[10px] text-[#A1A1AA] font-mono mt-0.5 truncate">
                          {item.name}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="w-8 h-8 rounded flex items-center justify-center hover:bg-[#FF3B30]/20 transition-colors"
                      >
                        <Trash size={16} className="text-[#FF3B30]" />
                      </button>
                    </div>
                  ))}
                  {formData.items.length === 0 && (
                    <p className="text-sm text-[#A1A1AA] text-center py-4 bg-[#1A1A1A] rounded-lg border border-dashed border-white/10">
                      Nenhum item adicionado.
                    </p>
                  )}
                </div>
              </div>

              {/* Add New Item */}
              <div className="border-t border-white/10 pt-4 mt-2">
                <label className="text-xs text-[#A1A1AA] block mb-2 ml-1">
                  Adicionar Novo Item
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={newItemLabel}
                    onChange={(e) => {
                      setNewItemLabel(e.target.value);
                      // Auto-generate name from label
                      setNewItemName(
                        e.target.value
                          .toLowerCase()
                          .replace(/\s+/g, "_")
                          .replace(/[^a-z0-9_]/g, ""),
                      );
                    }}
                    className="input-field h-12 w-full"
                    placeholder="Ex: Pneus calibrados"
                    data-testid="new-item-label"
                  />
                  <button
                    type="button"
                    onClick={addItem}
                    className="h-12 px-6 bg-[#222] text-[#00E676] hover:bg-[#2A2A2A] rounded-lg font-medium flex items-center justify-center gap-2 transition-colors border border-[#00E676]/20 shrink-0 w-full sm:w-auto"
                    data-testid="add-item-button"
                  >
                    <Plus size={18} weight="bold" />
                    <span>Adicionar</span>
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="btn-primary w-full mt-6 py-3"
                data-testid="save-template-button"
              >
                {editingTemplate ? "Salvar Alterações" : "Criar Template"}
              </button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
